import { AppDate } from '../envTypes';

import { ModelBase, ShowCustomization, ShipmentProvider } from './base';
import { Profile } from './profile';
import { ShipmentParcel, BaseOrder } from './order';
import { UpcomingShow } from './upcoming-show';
import { Listing } from './listing';
import { Address } from './address';
import { LiveStream } from './livestream';
import { SellerApplicationStatus } from './seller-application';

export interface ShowCover {
  name: string;
  imageUrl: string;
}

export interface WatchList {
  upcomingShow: UpcomingShow;
  createdAt: AppDate;
  _addedByCloudFunctionWhenFollowingSeller?: boolean;
}

// /showEvents/{eventId}
export interface ShowEvent {
  name: string;
  urlCode: string;
  previewVideoURL?: string;
  description?: string;
  backgroundURL?: string;
  verticalBackgroundURL?: string;
  active: boolean;
  sellerUserGroupIds: string[];
  // can user see the upcoming show and live show in this event
  // if they can see the show, but not in the user group of the show
  // (UpcomingShow -> userGroupIds) (LiveStream -> userGroupIds)
  // they will show 'require ticket' when open such show.
  // userGroupIds mean that user has purchased the event ticket so they
  // have access to the event.
  userGroupIds: string[];
  createdAt: AppDate;
  salesTaxRate?: number | null;
  maximumPurchaseQuantity?: number | null;
  startedAfter?: AppDate | null;
  endedBefore?: AppDate | null;
  shippingFee?: number;
  isClosed?: boolean;
  // can user see the event in the discovery page
  // only user in this group will see the event
  // users in the visibilityGroupIds will be able to see the event
  // and making ticket purchase
  visibilityGroupIds?: string[] | null;
  sortedAt?: AppDate | null;
  commissionFeeRate?: number | null;
  extraCommissionFeeRate?: number | null;
  featuredStoreStackName?: string | null;
  sellerApplicationDisclosure?: string;
  sellerApplicationDisclosureUrl?: string;

  unBundleOnDiscovery?: boolean;
  visibilityOnDiscoveryBanner?: boolean;
  visibleOnCategoryBanner?: boolean;
  categoryCoverUrl?: string;

  /**
   * @deprecated replaced by `showCustomization`
   */
  customLightingBoltImages?: string[];
  showCustomization?: ShowCustomization;
  showCover?: boolean;
}

export interface PrivateShowEvent {
  parcel: ShipmentParcel;
  shipmentProvider?: ShipmentProvider | null;
}

export interface EventSettingTemplate
  extends Pick<ShowEvent, 'commissionFeeRate' | 'salesTaxRate' | 'shippingFee'>,
    PrivateShowEvent,
    ModelBase {
  name: string;
  description?: string;
}

// /showEvents/{eventId}/tickets/{ticketId}
export interface ShowTicket
  extends Pick<
    Listing,
    | 'name'
    | 'defaultImage'
    | 'description'
    | 'createdAt'
    | 'price'
    | 'discountPrice'
    | 'sku'
  > {
  active: boolean;
  requireShipping: boolean;
  shippingFee?: number;
  userGroupIds: string[];
  quantity?: number;
  soldQuantity?: number;
  maximumPurchaseQuantity?: number;
  upgradeTicket?: boolean | null;
  fromTicketId?: string | null;
  toTicketId?: string | null;
  emailConfirmationDisclosure?: string;
  emailConfirmationDisclosureUrl?: string;
  emailConfirmationHeaderBackgroundUrl?: string;
  sortedAt?: AppDate;
}

// /showEvents/{eventId}/ticketOrders/{showTicketOrderId}
export interface ShowTicketOrder extends BaseOrder {
  showEventId: string;
  showEvent: ShowEvent;
  showTicketId: string;
  showTicket: ShowTicket;
  shippingAddress?: Address | null;
}

// /showEvents/{eventId}/sellerApplications/{id}
// /showEvents/{eventId}/sponsorApplications/{id}
// used for both seller and sponsorApplication
export interface ShowEventSellerApplication {
  createdAt: AppDate;
  sellerId: string;
  seller: Profile;
  status: SellerApplicationStatus;
  sellerUserGroupIds: string[];
  templateId: string;
}

// /showEvents/{eventId}/sponsors/{id}
export interface ShowEventSponsor {
  /**
   * @deprecated replaced by `sortedAt` since 11/30/20
   */
  position: number;
  sortedAt?: AppDate;
  createdAt: AppDate;
  sellerId: string;
  seller: Profile;
  sellerUserGroupIds: string[];
  templateId: string;
}

// /showEvents/{eventId}/sellerApplicationsTemplates/{id}
export interface ShowEventSellerApplicationTemplate {
  name: string;
  createdAt: AppDate;
  sellerUserGroupIds: string[];
  description?: string;
  // eventId: string;
  active: boolean;
  image?: string;
}

// /showEvents/{eventId}/templates/
export interface ShowEventTemplate {
  type: 'timeRange';
  name: string;
  userGroupIds: string[];
  startedAfter: AppDate;
  endedBefore: AppDate;
  createdAt: AppDate;
}

// /featuredShows/{id}
// /showEvents/{eventId}/featuredShows/{id}
export enum FeaturedShowType {
  upcomingShow = 'upcomingShow',
  liveShow = 'liveShow',
  event = 'event',
}

export enum FeaturedEvent {
  autoFeaturedStoreShow = 'autoFeaturedStoreShow',
}

export interface BaseFeaturedShow {
  sortedAt: AppDate;
  type: FeaturedShowType;
  active: boolean;
  featuredEvent?: FeaturedEvent;
}

export interface UpcomingFeaturedShow extends BaseFeaturedShow {
  type: FeaturedShowType.upcomingShow;
  upcomingShow: UpcomingShow;
}

export interface LiveShowFeaturedShow extends BaseFeaturedShow {
  type: FeaturedShowType.liveShow;
  liveStream: LiveStream;
}

export interface EventFeaturedShow extends BaseFeaturedShow {
  type: FeaturedShowType.event;
  event: ShowEvent;
}

export type FeaturedShow =
  | UpcomingFeaturedShow
  | LiveShowFeaturedShow
  | EventFeaturedShow;

export interface ScheduleNextShowPage {
  video: string;
  title: string;
  coverImage: string;
  enabled: boolean;
}

// CategorizedUpcomingShow, CategorizedLiveStream, CategorizedStore
// are exported from ./category.ts (they extend CategorizedResourceBase there)
